import React, { useRef, useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { motion } from 'framer-motion';

const IPhoneMockup = ({ videoSrc }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  
  // Ensure video plays and loops properly
  useEffect(() => {
    if (videoRef.current && isPlaying) {
      videoRef.current.play().catch(error => {
        console.error("Video playback failed:", error);
      });
    } else if (videoRef.current && !isPlaying) {
      videoRef.current.pause();
    }
  }, [videoSrc, isPlaying]);
  
  const togglePlayback = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '380px',
        margin: '0 auto',
      }}
    >
      <motion.div
        animate={{
          y: [-5, 5, -5],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          repeatType: 'reverse',
          ease: 'easeInOut',
        }}
        style={{
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* iPhone 15 Pro Frame */}
        <Box
          sx={{
            position: 'relative',
            width: { xs: '280px', sm: '320px', md: '340px' },
            height: { xs: '570px', sm: '650px', md: '690px' },
            margin: '0 auto',
            borderRadius: '50px',
            padding: '12px',
            background: 'linear-gradient(145deg, #1a1a1a, #2a2a2a)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '50px',
              border: '1px solid rgba(255,255,255,0.1)',
              zIndex: 2,
              pointerEvents: 'none',
            },
          }}
        >
          {/* Dynamic Island */}
          <Box
            sx={{
              position: 'absolute',
              top: '15px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '120px',
              height: '30px',
              borderRadius: '20px',
              backgroundColor: '#000',
              zIndex: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: 'rgba(60, 60, 60, 0.8)',
                marginRight: '50px',
              }}
            />
          </Box>

          {/* Screen */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              borderRadius: '40px',
              overflow: 'hidden',
              backgroundColor: '#000',
            }}
          >
            {/* Video */}
            <Box
              component="video"
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              src={videoSrc}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '40px',
              }}
            />

            {/* Control Button */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                display: 'flex',
                justifyContent: 'center',
              }}
              onClick={togglePlayback}
            >
              <IconButton
                sx={{
                  backgroundColor: isPlaying ? 'rgba(60, 223, 97, 0.9)' : 'rgba(255, 255, 255, 0.2)',
                  width: '40px',
                  height: '40px',
                  '&:hover': {
                    backgroundColor: isPlaying ? 'rgba(60, 223, 97, 1)' : 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                {isPlaying ? (
                  <Box
                    sx={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: 'white',
                      borderRadius: '2px',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 0,
                      height: 0,
                      borderTop: '6px solid transparent',
                      borderBottom: '6px solid transparent',
                      borderLeft: '10px solid white',
                      ml: '2px',
                    }}
                  />
                )}
              </IconButton>
            </Box>
            
            {/* Screen Reflection Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, transparent 40%, rgba(0,0,0,0.1) 100%)',
                borderRadius: '40px',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            />
          </Box>

          {/* Side Button */}
          <Box
            sx={{
              position: 'absolute',
              right: '-2px',
              top: '100px',
              width: '3px',
              height: '60px',
              backgroundColor: '#222',
              borderRadius: '3px 0 0 3px',
              zIndex: 5,
            }}
          />

          {/* Volume Buttons */}
          <Box
            sx={{
              position: 'absolute',
              left: '-2px',
              top: '100px',
              width: '3px',
              height: '30px',
              backgroundColor: '#222',
              borderRadius: '0 3px 3px 0',
              zIndex: 5,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: '-2px',
              top: '140px',
              width: '3px',
              height: '30px',
              backgroundColor: '#222',
              borderRadius: '0 3px 3px 0',
              zIndex: 5,
            }}
          />

          {/* Action Button */}
          <Box
            sx={{
              position: 'absolute',
              left: '-2px',
              top: '190px',
              width: '3px',
              height: '40px',
              backgroundColor: '#222',
              borderRadius: '0 3px 3px 0',
              zIndex: 5,
            }}
          />
        </Box>
      </motion.div>

      {/* Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '200%',
          height: '200%',
          top: '-50%',
          left: '-50%',
          background: 'radial-gradient(circle, rgba(66,165,245,0.1) 0%, rgba(25,118,210,0) 70%)',
          animation: 'pulseGradient 4s ease-in-out infinite alternate',
          zIndex: -1,
          '@keyframes pulseGradient': {
            '0%': { transform: 'scale(0.8)', opacity: 0.3 },
            '100%': { transform: 'scale(1.2)', opacity: 0.6 },
          },
        }}
      />

      {/* Bottom Glow */}
      <Box
        sx={{
          position: 'absolute',
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(25,118,210,0.2) 0%, rgba(25,118,210,0) 70%)',
          bottom: '-50px',
          left: '50%',
          transform: 'translateX(-50%)',
          filter: 'blur(20px)',
          zIndex: 0,
        }}
      />
    </motion.div>
  );
};

export default IPhoneMockup;
